import { Button, Form } from "react-bootstrap";
import { React, useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { getFooterById, updateFooter, createFooter } from "../../api/axios";
import {useNavigate, useParams} from "react-router-dom";
import CmsCircularProgress from "../../Components/Common/CircularProgress";
import { showToast } from "../../Utills/toast";
import FormGroup from "../../Components/Common/FormGroup";
import { Formik } from "formik";
import * as Yup from "yup";
import PhoneNumberInput from "../../Components/Common/PhoneInput/PhoneNumberInput";
import { getFileNameFromUrl } from "../../Utills/fileNameFromUrl";

// Validation schema
const validationSchema = Yup.object({
  paragraph: Yup.string().required("Paragraph is required"),
  mailing_address: Yup.string().required("Mailing address is required"),
  phoneNo: Yup.string().required("Phone number is required"),
  email_address: Yup.string().required("Email address is required"),
  faq: Yup.string().required("FAQ is required"),
  privacy_policy: Yup.mixed().required("Privacy policy is required"),
  facebook: Yup.string().required("Facebook is required"),
  twitter: Yup.string().required("Twitter is required"),
  linkedin: Yup.string().required("Linkedin is required"),
  instagram: Yup.string().required("Instagram is required"),
  pinterest: Yup.string().required("Pinterest is required"),
});

export default function FooterEdit() {
  const { partnerId, id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const formikRef = useRef(null);
  
  const fetchFooterData = async (footerId) => {
    if (!Number(footerId)) return;
    
    setIsLoading(true);
    try {
      const res = await getFooterById(footerId);
      const footerData = res.data.data[0];
      console.log("footerData ==> ", footerData);
      
      const formData = {
        paragraph: footerData.paragraph || "",
        mailing_address: footerData.mailing_address || "",
        phoneNo: footerData.phoneNo || "",
        email_address: footerData.email_address || "",
        faq: footerData.faq || "",
        privacy_policy: footerData.privacy_policy || "",
        facebook: footerData.facebook || "",
        twitter: footerData.twitter || "",
        linkedin: footerData.linkedin || "",
        instagram: footerData.instagram || "",
        pinterest: footerData.pinterest || "",
      };
      
      setInitialData(formData);
      
      // Reset Formik with new data if component is mounted
      if (formikRef.current) {
        formikRef.current.resetForm({
          values: formData
        });
      }
    } catch (err) {
      showToast({
        type: 'error',
        message: 'Failed to fetch footer details. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFooterData(id);
  }, [id]);

  const handleSubmit = async (values, { setSubmitting }) => {
    console.log("Form values:", values);
    console.log("setSubmitting ==> ", setSubmitting);
    try {
      const formData = new FormData();
      const dataToSend = {
        paragraph: values.paragraph,
        mailing_address: values.mailing_address,
        phoneNo: values.phoneNo,
        email_address: values.email_address,
        faq: values.faq,
        privacy_policy: values.privacy_policy,
        facebook: values.facebook,
        twitter: values.twitter,
        linkedin: values.linkedin,
        instagram: values.instagram,
        pinterest: values.pinterest,
      };

      console.log("Data to send:", dataToSend);

      for (const [key, value] of Object.entries(dataToSend)) {
        formData.append(key, value);
      }

      let res;
      if (!Number(id)) {
        formData.append("partner_id", partnerId);
        res = await createFooter(formData);
        if (res.status === 200) {
          showToast({
            type: 'success',
            message: 'Footer details have been created successfully!',
          });
          console.log(res.data);
        } else {
          showToast({
            type: 'error',
            message: 'Failed to create footer details. Please try again.',
          });
        }
      } else {
        res = await updateFooter(id, formData);
        if (res.status === 200) {
          showToast({
            type: 'success',
            message: 'Footer details have been updated successfully!',
          });
          console.log(res.data);
        } else {
          showToast({
            type: 'error',
            message: 'Failed to update footer details. Please try again.',
          });
        }
      }
    } catch (err) {
      showToast({
        type: 'error',
        message: !Number(id)
          ? 'Failed to create footer details. Please try again.'
          : 'Failed to update footer details. Please try again.',
      });
      console.log(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClick = () => {
    navigate(`/${partnerId}/footer`);
  };

  const getInitialValues = () => ({
    paragraph: "",
    mailing_address: "",
    phoneNo: "",
    email_address: "",
    faq: "",
    privacy_policy: "",
    facebook: "",
    twitter: "",
    linkedin: "",
    instagram: "",
    pinterest: "",
  });

  return (
    <>
    {isLoading ? <CmsCircularProgress/> : 
    <FormWrapper>
      <SliderDiv>
        <Formik
          innerRef={formikRef}
          initialValues={initialData || getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ handleSubmit, getFieldProps, setFieldValue, values, errors, touched, isSubmitting }) => (
            <Form onSubmit={handleSubmit} noValidate>
              <Title>{!Number(id) ? "Create" : "Edit"} Footer details</Title>
              <FormGroup 
                label='Paragraph' 
                requiredField
                error={touched.paragraph && errors.paragraph}
              >
                <Form.Control
                  as="textarea"
                  style={{ height: "auto", minHeight: "180px" }}
                  placeholder="Enter paragraph"
                  {...getFieldProps("paragraph")}
                  isInvalid={touched.paragraph && errors.paragraph}
                />
              </FormGroup>

              <FormGroup 
                label='Mailing Address'
                requiredField
                error={touched.mailing_address && errors.mailing_address}
              >
                <Form.Control
                  type="text"
                  style={{ height: "40px" }}
                  placeholder="Enter Mailing Address"
                  {...getFieldProps("mailing_address")}
                  isInvalid={touched.mailing_address && errors.mailing_address}
                />
              </FormGroup>
              
              <FormGroup 
                label='Phone Number'
                requiredField
                error={touched.phoneNo && errors.phoneNo}
              >

                <PhoneNumberInput
                  label='Phone Number'
                  name="phone_number"
                  value={values.phoneNo}
                  onChange={(value, country) => {
                    setFieldValue("phoneNo", value);
                  }}
                />        
                {/* <Form.Control
                  type="text"
                  style={{ height: "40px" }}
                  placeholder="Enter Phone Number"
                  {...getFieldProps("phoneNo")}
                  isInvalid={touched.phoneNo && errors.phoneNo}
                /> */}
              </FormGroup>
              
              <FormGroup 
                label='Email'
                requiredField
                error={touched.email_address && errors.email_address}
              >
                <Form.Control
                  type="email"
                  style={{ height: "40px" }}
                  placeholder="Enter Email"
                  {...getFieldProps("email_address")}
                  isInvalid={touched.email_address && errors.email_address}
                />
              </FormGroup>

              <FormGroup 
                label='FAQ'
                requiredField
                error={touched.faq && errors.faq}
                fileName={getFileNameFromUrl(values.faq)}
              >
                {/* Show current file name if editing */}
                <Form.Control
                  name="faq"
                  type="file"
                  accept=".pdf"
                  onChange={(e) => {
                    setFieldValue("faq", e.target.files[0]);
                  }}
                  isInvalid={touched.faq && errors.faq}
                />
              </FormGroup>
              <FormGroup 
                label='Privacy Policy'
                requiredField
                error={touched.privacy_policy && errors.privacy_policy}
                fileName={getFileNameFromUrl(values.privacy_policy)}
              >
                <Form.Control
                  name="privacy_policy"
                  type="file"
                  onChange={(e) => {
                    setFieldValue("privacy_policy", e.target.files[0]);
                  }}
                  isInvalid={touched.privacy_policy && errors.privacy_policy}
                />
              </FormGroup>

              <FormGroup 
                label='Social Media'
                requiredField
              >
                <TableWrapper>
                  <Table>
                    <thead>
                      <TableRow>
                        <TableHeader>Social Media</TableHeader>
                        <TableHeader>Link</TableHeader>
                      </TableRow>
                    </thead>
                    <TableBody>
                      {[
                        { name: 'Facebook', field: 'facebook' },
                        { name: 'Twitter', field: 'twitter' },
                        { name: 'Linkedin', field: 'linkedin' },
                        { name: 'Instagram', field: 'instagram' },
                        { name: 'Pinterest', field: 'pinterest' }
                      ].map((social) => (
                        <TableRow key={social.field}>
                          <TableCell>{social.name}</TableCell>
                          <TableCell>
                           <FormGroup error={touched[social.field] && errors[social.field]}>
                           <Form.Control
                                type="text"
                                style={{ height: "40px" }}
                                placeholder={`Enter your ${social.name} URL`}
                                {...getFieldProps(social.field)}
                                isInvalid={touched[social.field] && errors[social.field]}
                              />
                           </FormGroup>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableWrapper>
              </FormGroup>

              <ButtonWrapper>
                <Button
                  variant="secondary"
                  onClick={handleClick}
                  style={{ marginTop: "20px", width: "150px" }}
                >
                  Go Back
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  style={{ marginTop: "20px", width: "150px" }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </SliderDiv>
    </FormWrapper>}
    </>
  );
}

const SliderDiv = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;

const FormWrapper = styled.div`
  @media (min-width:576px) {
   padding: 1rem;
  }
    @media (min-width:768px) {
   padding: 1.5rem;
  }
   padding: .5rem;
`;
const Title = styled.h3`
  text-align: center;
`;

const TableWrapper = styled.div`
  margin: 0 auto;

  .textarea {
    box-sizing: fixed;
  }
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f2f2f2;
  }
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 10px;
  border: 2px solid #ccc;
`;

const TableBody = styled.tbody``;

const ButtonWrapper = styled.tbody`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const TableCell = styled.td`
  padding: 10px;
  border: 1px solid #ddd;
`;
